import { useRef } from 'react';
import { useMergedRef } from '@mantine/hooks';
import {
  Box,
  BoxProps,
  createVarsResolver,
  ElementProps,
  factory,
  Factory,
  StylesApiProps,
  useProps,
  useStyles,
} from '../../core';
import classes from './FloatingIndicator.module.css';
import { useFloatingIndicator } from './use-floating-indicator';

export type FloatingIndicatorStylesNames = 'root';
export type FloatingIndicatorCssVariables = {
  root: '--transition-duration';
};

export interface FloatingIndicatorProps
  extends BoxProps,
    StylesApiProps<FloatingIndicatorFactory>,
    ElementProps<'div'> {
  /** Target element over which indicator is displayed */
  target: HTMLElement | null | undefined;

  /** Parent element with relative position based on which indicator position is calculated */
  parent: HTMLElement | null | undefined;

  /** Transition duration in ms @default `150` */
  transitionDuration?: number | string;

  /** If set, the indicator is displayed after transition ends.
   * Should be set if the component is used inside a container that has `transform: scale(n)` styles.
   * */
  displayAfterTransitionEnd?: boolean;
}

export type FloatingIndicatorFactory = Factory<{
  props: FloatingIndicatorProps;
  ref: HTMLDivElement;
  stylesNames: FloatingIndicatorStylesNames;
  vars: FloatingIndicatorCssVariables;
}>;

const varsResolver = createVarsResolver<FloatingIndicatorFactory>(
  (_theme, { transitionDuration }) => ({
    root: {
      '--transition-duration':
        typeof transitionDuration === 'number' ? `${transitionDuration}ms` : transitionDuration,
    },
  })
);

export const FloatingIndicator = factory<FloatingIndicatorFactory>((_props, ref) => {
  const props = useProps('FloatingIndicator', null, _props);
  const {
    classNames,
    className,
    style,
    styles,
    unstyled,
    vars,
    target,
    parent,
    transitionDuration,
    mod,
    displayAfterTransitionEnd,
    attributes,
    ...others
  } = props;

  const getStyles = useStyles<FloatingIndicatorFactory>({
    name: 'FloatingIndicator',
    classes,
    props,
    className,
    style,
    classNames,
    styles,
    unstyled,
    attributes,
    vars,
    varsResolver,
  });

  const innerRef = useRef<HTMLDivElement>(null);
  const { initialized, hidden } = useFloatingIndicator({
    target,
    parent,
    ref: innerRef as any,
    displayAfterTransitionEnd,
  });

  const mergedRef = useMergedRef(ref, innerRef);

  if (!target || !parent) {
    return null;
  }

  return (
    <Box ref={mergedRef} mod={[{ initialized, hidden }, mod]} {...getStyles('root')} {...others} />
  );
});

FloatingIndicator.displayName = '@mantine/core/FloatingIndicator';
FloatingIndicator.classes = classes;
